<template>
  <div class="container mt-5" style="max-width: 600px;">
    <!-- 테스트 데이터 생성 카드 -->
    <div class="card shadow-sm mb-4">
      <div class="card-body p-4">
        <h4 class="card-title mb-4 fw-bold">테스트 데이터 생성</h4>

        <div class="mb-3">
          <label class="form-label">생성할 회원 수</label>
          <input
            v-model.number="count"
            type="number"
            class="form-control"
            min="1"
            max="1000"
            placeholder="생성할 회원 수 입력 (1-1000)"
          />
        </div>

        <div class="mb-3">
          <label class="form-label">전화번호</label>
          <input
            v-model="testUserTel"
            type="text"
            class="form-control"
            placeholder="전화번호 입력 (예: 010-6420-3694)"
          />
          <small class="text-muted">입력한 전화번호가 모든 회원에 적용됩니다.</small>
        </div>

        <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>
        <div v-if="successMessage" class="alert alert-success py-2 mb-3">{{ successMessage }}</div>

        <div v-if="progress.total > 0" class="mb-3">
          <div class="d-flex justify-content-between mb-2">
            <span>진행 상황</span>
            <span>{{ progress.current }} / {{ progress.total }}</span>
          </div>
          <div class="progress">
            <div
              class="progress-bar"
              role="progressbar"
              :style="{ width: progress.percentage + '%' }"
              :aria-valuenow="progress.percentage"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{ progress.percentage }}%
            </div>
          </div>
        </div>

        <button
          class="btn btn-primary w-100"
          :disabled="loading || !count || count < 1"
          @click="createTestUsers"
        >
          {{ loading ? '처리 중...' : '테스트 회원 생성' }}
        </button>
      </div>
    </div>

    <!-- 자동 정기권 대기 신청 카드 -->
    <div class="card shadow-sm mb-4">
      <div class="card-body p-4">
        <h4 class="card-title mb-4 fw-bold">자동 정기권 대기 신청</h4>

        <div class="mb-3">
          <label class="form-label">주차장 선택 <span class="text-danger">*</span></label>
          <select v-model="waitingForm.areaSeq" class="form-select" :disabled="loadingWaiting || areas.length === 0">
            <option value="">주차장을 선택하세요</option>
            <option v-for="a in areas" :key="a.AreaSeq" :value="a.AreaSeq">
              {{ a.AreaName }} ({{ a.AreaCode }})
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">적용할 test 회원 수</label>
          <div class="alert alert-info py-2 small">
            현재 test로 시작하는 회원: <strong>{{ testUserCount }}</strong>명
          </div>
        </div>

        <div v-if="errorWaiting" class="alert alert-danger py-2 mb-3">{{ errorWaiting }}</div>
        <div v-if="successMessageWaiting" class="alert alert-success py-2 mb-3">{{ successMessageWaiting }}</div>

        <div v-if="progressWaiting.total > 0" class="mb-3">
          <div class="d-flex justify-content-between mb-2">
            <span>진행 상황</span>
            <span>{{ progressWaiting.current }} / {{ progressWaiting.total }}</span>
          </div>
          <div class="progress">
            <div
              class="progress-bar bg-success"
              role="progressbar"
              :style="{ width: progressWaiting.percentage + '%' }"
              :aria-valuenow="progressWaiting.percentage"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{ progressWaiting.percentage }}%
            </div>
          </div>
        </div>

        <button
          class="btn btn-success w-100"
          :disabled="loadingWaiting || !waitingForm.areaSeq || testUserCount === 0"
          @click="createBulkWaitingRegistration"
        >
          {{ loadingWaiting ? '처리 중...' : '일괄 대기 신청' }}
        </button>
      </div>
    </div>

    <!-- 데이터 삭제 카드 -->
    <!-- 당첨자 결제 완료 일괄처리 -->
    <div class="card shadow-sm mb-4">
      <div class="card-body p-4">
        <h4 class="card-title mb-4 fw-bold">당첨자 결제완료 일괄처리</h4>

        <div class="mb-3">
          <label class="form-label">주차장 선택 <span class="text-danger">*</span></label>
          <select v-model="winnersForm.areaSeq" class="form-select" :disabled="loadingWinnersProcessing || areas.length === 0">
            <option value="">주차장을 선택하세요</option>
            <option v-for="a in areas" :key="a.AreaSeq" :value="a.AreaSeq">
              {{ a.AreaName }} ({{ a.AreaCode }})
            </option>
          </select>
        </div>

        <div v-if="errorWinners" class="alert alert-danger py-2 mb-3 white-space-pre-wrap">{{ errorWinners }}</div>
        <div v-if="successMessageWinners" class="alert alert-success py-2 mb-3">{{ successMessageWinners }}</div>

        <div v-if="progressWinners.total > 0" class="mb-3">
          <div class="d-flex justify-content-between mb-2">
            <span>당첨자 처리 진행</span>
            <span>{{ progressWinners.current }} / {{ progressWinners.total }}</span>
          </div>
          <div class="progress">
            <div
              class="progress-bar bg-success"
              role="progressbar"
              :style="{ width: progressWinners.percentage + '%' }"
              :aria-valuenow="progressWinners.percentage"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{ progressWinners.percentage }}%
            </div>
          </div>
        </div>

        <button
          class="btn btn-success w-100 mb-2"
          :disabled="loadingWinnersProcessing || !winnersForm.areaSeq"
          @click="markWinnersAsPaid"
        >
          {{ loadingWinnersProcessing ? '처리 중...' : '당첨자 결제완료 처리' }}
        </button>
      </div>
    </div>

    <!-- 데이터 삭제 카드 -->
    <div class="card shadow-sm border-danger mb-4">
      <div class="card-body p-4">
        <h4 class="card-title mb-4 fw-bold text-danger">테스트 데이터 삭제</h4>

        <div class="alert alert-warning py-2 mb-3">
          <strong>주의:</strong> 삭제된 데이터는 복구할 수 없습니다.
        </div>

        <div v-if="errorDelete" class="alert alert-danger py-2 mb-3">{{ errorDelete }}</div>
        <div v-if="successMessageDelete" class="alert alert-success py-2 mb-3">{{ successMessageDelete }}</div>

        

        <div v-if="progressDelete.total > 0" class="mb-3">
          <div class="d-flex justify-content-between mb-2">
            <span>진행 상황</span>
            <span>{{ progressDelete.current }} / {{ progressDelete.total }}</span>
          </div>
          <div class="progress">
            <div
              class="progress-bar bg-danger"
              role="progressbar"
              :style="{ width: progressDelete.percentage + '%' }"
              :aria-valuenow="progressDelete.percentage"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{ progressDelete.percentage }}%
            </div>
          </div>
        </div>

        <div class="d-grid gap-2">
          
          <button
            class="btn btn-outline-danger"
            :disabled="loadingDelete || testUserCount === 0"
            @click="deleteAllWaitingData"
          >
            {{ loadingDelete ? '처리 중...' : `정기권 대기 신청 데이터 삭제 (${testUserCount}명)` }}
          </button>
          <button
            class="btn btn-danger"
            :disabled="loadingDelete || testUserCount === 0"
            @click="deleteAllTestUsers"
          >
            {{ loadingDelete ? '처리 중...' : `사용자 데이터 삭제 (${testUserCount}명)` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminTestDataInsertView',
  data() {
    return {
      count: null,
      loading: false,
      error: null,
      successMessage: null,
      progress: { current: 0, total: 0, percentage: 0 },
      usedCarNumbers: new Set(),
      areas: [],
      testUsers: [],
      waitingForm: { areaSeq: '' },
      winnersForm: { areaSeq: '' },
      loadingWaiting: false,
      errorWaiting: null,
      successMessageWaiting: null,
      progressWaiting: { current: 0, total: 0, percentage: 0 },
      loadingDelete: false,
      errorDelete: null,
      successMessageDelete: null,
      progressDelete: { current: 0, total: 0, percentage: 0 }
      ,
      // 당첨자 결제 처리
      loadingWinnersProcessing: false,
      progressWinners: { current: 0, total: 0, percentage: 0 },
      successMessageWinners: null,
      errorWinners: null,
      // 테스트 회원 생성시 사용할 전화번호
      testUserTel: '010-6420-3694'
    }
  },
  computed: {
    testUserCount() {
      return this.testUsers.length
    }
  },
  mounted() {
    this.initializeData()
  },
  methods: {
    async initializeData() {
      try {
        await Promise.all([this.fetchAreas(), this.fetchTestUsers()])
      } catch (e) {
        console.error('initializeData error:', e)
      }
    },
    async markWinnersAsPaid() {
      this.errorWinners = null
      this.successMessageWinners = null

      if (!this.winnersForm.areaSeq) {
        this.errorWinners = '주차장을 선택하세요.'
        return
      }

      if (this.testUsers.length === 0) {
        this.errorWinners = 'test로 시작하는 회원이 없습니다.'
        return
      }

      if (!confirm('선택한 주차장의 당첨자들을 결제완료 상태로 변경하시겠습니까?')) return

      this.loadingWinnersProcessing = true
      this.progressWinners.current = 0
      this.progressWinners.total = 0

      try {
        const area = this.areas.find(a => a.AreaSeq === parseInt(this.winnersForm.areaSeq))
        if (!area) {
          this.errorWinners = '선택한 주차장을 찾을 수 없습니다.'
          this.loadingWinnersProcessing = false
          return
        }

        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting?areaSeq=${this.winnersForm.areaSeq}`)
        const waitings = res.data || []
        const capacity = area.Capacity || 0

        const winners = waitings.filter(w => w.WaitingNumber && w.WaitingNumber <= capacity)

        if (winners.length === 0) {
          this.successMessageWinners = '당첨자가 없습니다.'
          this.loadingWinnersProcessing = false
          return
        }

        this.progressWinners.total = winners.length

        const results = { success: 0, failed: 0, errors: [] }

        for (let i = 0; i < winners.length; i++) {
          const w = winners[i]
          try {
            await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/complete-payment`, {
              waitingSeq: w.WaitingSeq
            })
            results.success++
          } catch (e) {
            results.failed++
            results.errors.push(`${w.WaitingSeq}: ${e.response?.data?.message || '오류'}`)
          }

          this.progressWinners.current = i + 1
          this.progressWinners.percentage = Math.round(((i + 1) / winners.length) * 100)
        }

        if (results.failed === 0) {
          this.successMessageWinners = `✓ ${results.success}명의 당첨자가 결제완료 처리되었습니다.`
        } else {
          this.successMessageWinners = `처리 완료 - 성공: ${results.success}명, 실패: ${results.failed}명`
          this.errorWinners = results.errors.slice(0, 5).join('\n')
        }

        await this.fetchTestUsers()
      } catch (e) {
        console.error('markWinnersAsPaid error:', e)
        this.errorWinners = e.response?.data?.message || '처리 중 오류가 발생했습니다.'
      } finally {
        this.loadingWinnersProcessing = false
        setTimeout(() => {
          this.progressWinners.total = 0
          this.progressWinners.current = 0
          this.progressWinners.percentage = 0
        }, 1500)
      }
    },
    generateRandomPhone() {
      const part1 = String(Math.floor(Math.random() * 10000)).padStart(4, '0')
      const part2 = String(Math.floor(Math.random() * 10000)).padStart(4, '0')
      return `010-${part1}-${part2}`
    },
    generateRandomCarNumber() {
      const koreanChars = ['가', '나', '다', '라', '마', '바', '사', '아', '자', '차', '카', '타', '파', '하']
      let carNumber
      do {
        const frontNum = String(Math.floor(Math.random() * 100)).padStart(2, '0')
        const koreanChar = koreanChars[Math.floor(Math.random() * koreanChars.length)]
        const backNum = String(Math.floor(Math.random() * 10000)).padStart(4, '0')
        carNumber = `${frontNum}${koreanChar}${backNum}`
      } while (this.usedCarNumbers.has(carNumber))
      this.usedCarNumbers.add(carNumber)
      return carNumber
    },
    async createTestUsers() {
      this.error = null
      this.successMessage = null

      if (!this.count || this.count < 1) {
        this.error = '생성할 회원 수를 1 이상으로 입력하세요.'
        return
      }

      if (this.count > 1000) {
        this.error = '한 번에 최대 1000명까지만 생성할 수 있습니다.'
        return
      }

      this.loading = true
      this.usedCarNumbers.clear()
      this.progress.total = this.count
      this.progress.current = 0

      const results = { success: 0, failed: 0, errors: [] }

      for (let i = 1; i <= this.count; i++) {
        try {
          const userId = `test${i}`
          await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/test-register`, {
            id: userId,
            password: 'test',
            name: userId,
            tel: this.testUserTel,
            carNumber: this.generateRandomCarNumber()
          })
          results.success++
        } catch (e) {
          results.failed++
          results.errors.push(`test${i}: ${e.response?.data?.message || '알 수 없는 오류'}`)
        }

        this.progress.current = i
        this.progress.percentage = Math.round((i / this.count) * 100)
      }

      this.loading = false
      await this.fetchTestUsers()

      if (results.failed === 0) {
        this.successMessage = `✓ 테스트 회원 ${results.success}명이 성공적으로 생성되었습니다.`
      } else {
        this.successMessage = `테스트 회원 생성 완료 - 성공: ${results.success}명, 실패: ${results.failed}명`
        if (results.errors.length > 0) {
          this.error = `오류 발생:\n${results.errors.slice(0, 5).join('\n')}${results.errors.length > 5 ? '\n...' : ''}`
        }
      }

      setTimeout(() => {
        this.progress.total = 0
        this.progress.current = 0
        this.progress.percentage = 0
      }, 2000)
    },
    async fetchAreas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-areas`)
        this.areas = res.data || []
      } catch (e) {
        console.error('fetchAreas error:', e)
        this.areas = []
      }
    },
    async fetchTestUsers() {
      try {
        let allTestUsers = []
        let page = 1
        let hasMorePages = true

        while (hasMorePages) {
          const res = await axios.get(
            `${process.env.VUE_APP_API_BASE}/api/admin/users?search=test&page=${page}`
          )
          const users = res.data.data || []
          allTestUsers = allTestUsers.concat(users)

          // 다음 페이지가 있는지 확인
          hasMorePages = page < (res.data.totalPages || 0)
          page++
        }

        this.testUsers = allTestUsers.filter(u => u.UserID && u.UserID.startsWith('test'))
      } catch (e) {
        console.error('fetchTestUsers error:', e)
        this.testUsers = []
      }
    },
    async createBulkWaitingRegistration() {
      this.errorWaiting = null
      this.successMessageWaiting = null

      if (!this.waitingForm.areaSeq) {
        this.errorWaiting = '주차장을 선택하세요.'
        return
      }

      if (this.testUsers.length === 0) {
        this.errorWaiting = 'test로 시작하는 회원이 없습니다.'
        return
      }

      this.loadingWaiting = true
      this.progressWaiting.total = this.testUsers.length
      this.progressWaiting.current = 0

      try {
        const selectedArea = this.areas.find(a => a.AreaSeq === parseInt(this.waitingForm.areaSeq))
        if (!selectedArea) {
          this.errorWaiting = '선택한 주차장을 찾을 수 없습니다.'
          this.loadingWaiting = false
          return
        }

        const feesRes = await axios.get(
          `${process.env.VUE_APP_API_BASE}/api/admin/parking-fee?areaCode=${encodeURIComponent(selectedArea.AreaCode)}`
        )
        const fees = feesRes.data || []

        if (fees.length === 0) {
          this.errorWaiting = '해당 주차장의 요금제가 없습니다.'
          this.loadingWaiting = false
          return
        }

        const discountsRes = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/discount`)
        const discounts = discountsRes.data || []

        const results = { success: 0, failed: 0, errors: [] }

        for (let i = 0; i < this.testUsers.length; i++) {
          const user = this.testUsers[i]
          try {
            const randomFee = fees[Math.floor(Math.random() * fees.length)]
            let discountSeq = null
            if (discounts.length > 0 && Math.random() > 0.3) {
              const randomDiscount = discounts[Math.floor(Math.random() * discounts.length)]
              discountSeq = randomDiscount.DiscountSeq
            }

            await axios.post(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting`, {
              userSeq: user.UserSeq,
              areaSeq: parseInt(this.waitingForm.areaSeq),
              feeSeq: randomFee.FeeSeq,
              discountSeq: discountSeq
            })

            results.success++
          } catch (e) {
            results.failed++
            results.errors.push(`${user.UserID}: ${e.response?.data?.message || '알 수 없는 오류'}`)
          }

          this.progressWaiting.current = i + 1
          this.progressWaiting.percentage = Math.round(((i + 1) / this.testUsers.length) * 100)
        }

        this.loadingWaiting = false

        if (results.failed === 0) {
          this.successMessageWaiting = `✓ ${results.success}명의 test 회원이 정기권 대기 신청되었습니다.`
        } else {
          this.successMessageWaiting = `대기 신청 완료 - 성공: ${results.success}명, 실패: ${results.failed}명`
          if (results.errors.length > 0) {
            this.errorWaiting = `오류 발생:\n${results.errors.slice(0, 5).join('\n')}${results.errors.length > 5 ? '\n...' : ''}`
          }
        }

        setTimeout(() => {
          this.progressWaiting.total = 0
          this.progressWaiting.current = 0
          this.progressWaiting.percentage = 0
        }, 2000)
      } catch (e) {
        console.error('createBulkWaitingRegistration error:', e)
        this.errorWaiting = e.response?.data?.message || '일괄 대기 신청 중 오류가 발생했습니다.'
        this.loadingWaiting = false
      }
    },
    async deleteAllWaitingData() {
      if (!confirm(`test로 시작하는 ${this.testUserCount}명의 정기권 대기 신청 데이터를 삭제하시겠습니까?`)) {
        return
      }

      this.loadingDelete = true
      this.errorDelete = null
      this.successMessageDelete = null
      this.progressDelete.total = this.testUsers.length
      this.progressDelete.current = 0

      const results = { success: 0, failed: 0, errors: [] }

      for (let i = 0; i < this.testUsers.length; i++) {
        const user = this.testUsers[i]
        try {
          await axios.delete(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting?userSeq=${user.UserSeq}`)
          results.success++
        } catch (e) {
          results.failed++
          results.errors.push(`${user.UserID}: ${e.response?.data?.message || '알 수 없는 오류'}`)
        }

        this.progressDelete.current = i + 1
        this.progressDelete.percentage = Math.round(((i + 1) / this.testUsers.length) * 100)
      }

      this.loadingDelete = false

      if (results.failed === 0) {
        this.successMessageDelete = `✓ ${results.success}명의 정기권 대기 신청 데이터가 삭제되었습니다.`
      } else {
        this.successMessageDelete = `삭제 완료 - 성공: ${results.success}명, 실패: ${results.failed}명`
        if (results.errors.length > 0) {
          this.errorDelete = `오류 발생:\n${results.errors.slice(0, 5).join('\n')}${results.errors.length > 5 ? '\n...' : ''}`
        }
      }

      setTimeout(() => {
        this.progressDelete.total = 0
        this.progressDelete.current = 0
        this.progressDelete.percentage = 0
      }, 2000)
    },
    async deleteAllTestUsers() {
      if (!confirm(`test로 시작하는 ${this.testUserCount}명의 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
        return
      }

      this.loadingDelete = true
      this.errorDelete = null
      this.successMessageDelete = null
      this.progressDelete.total = this.testUsers.length
      this.progressDelete.current = 0

      const results = { success: 0, failed: 0, errors: [] }

      for (let i = 0; i < this.testUsers.length; i++) {
        const user = this.testUsers[i]
        try {
          await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/users/${user.UserSeq}`)
          results.success++
        } catch (e) {
          results.failed++
          results.errors.push(`${user.UserID}: ${e.response?.data?.message || '알 수 없는 오류'}`)
        }

        this.progressDelete.current = i + 1
        this.progressDelete.percentage = Math.round(((i + 1) / this.testUsers.length) * 100)
      }

      this.loadingDelete = false
      await this.fetchTestUsers()

      if (results.failed === 0) {
        this.successMessageDelete = `✓ ${results.success}명의 사용자가 삭제되었습니다.`
      } else {
        this.successMessageDelete = `삭제 완료 - 성공: ${results.success}명, 실패: ${results.failed}명`
        if (results.errors.length > 0) {
          this.errorDelete = `오류 발생:\n${results.errors.slice(0, 5).join('\n')}${results.errors.length > 5 ? '\n...' : ''}`
        }
      }

      setTimeout(() => {
        this.progressDelete.total = 0
        this.progressDelete.current = 0
        this.progressDelete.percentage = 0
      }, 2000)
    }
  }
}
</script>

<style scoped>
.progress {
  height: 25px;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 0.875rem;
}

.border-danger {
  border-color: #dc3545 !important;
}
</style>
